/**
 * budgetSheetMath.js
 *
 * Maths for Budget Sheets (the Budgets tab on Notes & Reminders). No UI here,
 * same rule as plansUtils/budgetMath: the page shows numbers, this file makes
 * them.
 *
 * Shape, stored on plans.budgetSheets:
 *
 *   { id, name, emoji, pinned, createdAt, updatedAt,
 *     sections: [{ id, title, showTotal,
 *                  rows: [{ id, name, amount, note, below, calc }] }] }
 *
 * `below` puts a row under the section's total line (shown, not counted).
 *
 * `calc` is null for a typed row. Otherwise it is one of
 *   { type: 'sum',   refs: [ref, ...] }             add these up
 *   { type: 'left',  from: ref, minus: [ref, ...] } what's left after...
 *   { type: 'share', of: ref, part: 'half'|'third'|'quarter'|'pct', pct }
 * and a ref is 'row:<rowId>' or 'sec:<sectionId>' (that section's total).
 * A worked-out row still keeps `amount` - the last value it showed - which is
 * what it falls back to if it is ever turned back into a typed row.
 *
 * THE ONE RULE: a row can only use things above it. The sheet is evaluated
 * top to bottom - each section's counted rows, then its total, then the rows
 * under its total line - and a ref is only ever looked up among values that
 * already exist. So a row can never depend on itself, and there are no loops
 * to detect: the order makes them impossible.
 *
 * Row and section ids only need to be unique within one sheet. That is what
 * lets Duplicate copy a sheet without rewriting any references inside it.
 */
import { formatCurrency } from './formatters';

export function newId(prefix) {
  return prefix + '_' + Math.random().toString(36).slice(2, 10);
}

export function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** '$1,374.50', '1374.5', ' 12 ' -> a number rounded to cents. Blank is 0. */
export function parseAmount(str) {
  const cleaned = String(str == null ? '' : str).replace(/[^0-9.-]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? round2(n) : 0;
}

/** In the person's own currency - the same formatter as the rest of the app. */
export function formatMoney(n) {
  return formatCurrency(round2(n));
}

export const rowRef = (id) => 'row:' + id;
export const secRef = (id) => 'sec:' + id;

const SHARE_FACTOR = { half: 0.5, third: 1 / 3, quarter: 0.25 };
const SHARE_LABEL = { half: '½', third: '⅓', quarter: '¼' };

export function shareFactor(calc) {
  if (!calc) return 0;
  if (calc.part === 'pct') return (Number(calc.pct) || 0) / 100;
  return SHARE_FACTOR[calc.part] || 0;
}

/** Every ref a calc reads. */
export function calcRefs(calc) {
  if (!calc) return [];
  if (calc.type === 'sum') return calc.refs || [];
  if (calc.type === 'left') return [calc.from, ...(calc.minus || [])].filter(Boolean);
  if (calc.type === 'share') return calc.of ? [calc.of] : [];
  return [];
}

/**
 * Value of one row, given only the values above it.
 * Returns { value, broken } - broken means a ref it needs isn't above it (or
 * no longer exists). Missing refs count as 0 so the sheet never shows an
 * error, and settleSheet() below turns such rows back into typed ones.
 */
function evalRow(row, values) {
  const c = row.calc;
  if (!c) return { value: round2(row.amount), broken: false };
  let broken = false;
  const get = (ref) => {
    if (!ref || !(ref in values)) { broken = true; return 0; }
    return values[ref];
  };
  let v = 0;
  if (c.type === 'sum') {
    v = (c.refs || []).reduce((n, r) => n + get(r), 0);
    if (!(c.refs || []).length) broken = true;
  } else if (c.type === 'left') {
    v = get(c.from) - (c.minus || []).reduce((n, r) => n + get(r), 0);
  } else if (c.type === 'share') {
    v = get(c.of) * shareFactor(c);
  } else {
    broken = true;
  }
  return { value: round2(v), broken };
}

/**
 * Every row's value and every section's total for one sheet, in the one
 * evaluation order. Rows under the total line are valued but never counted.
 */
export function computeSheet(sheet) {
  const values = {};
  const rows = {};
  const totals = {};
  const broken = {};
  for (const sec of (sheet && sheet.sections) || []) {
    const list = sec.rows || [];
    let t = 0;
    for (const r of list) {
      if (r.below) continue;
      const res = evalRow(r, values);
      values[rowRef(r.id)] = res.value;
      rows[r.id] = res.value;
      if (res.broken) broken[r.id] = true;
      t += res.value;
    }
    totals[sec.id] = round2(t);
    values[secRef(sec.id)] = totals[sec.id];
    for (const r of list) {
      if (!r.below) continue;
      const res = evalRow(r, values);
      values[rowRef(r.id)] = res.value;
      rows[r.id] = res.value;
      if (res.broken) broken[r.id] = true;
    }
  }
  return { rows, totals, broken, values };
}

/**
 * After any change, a worked-out row whose ingredients have gone (deleted, or
 * moved below it) is frozen: it becomes a typed row holding the amount it
 * showed BEFORE the change. The sheet never shows an error or a blank.
 * Returns { sheet, frozen: [row names] }.
 */
export function settleSheet(prevSheet, nextSheet) {
  const { broken } = computeSheet(nextSheet);
  const ids = Object.keys(broken);
  if (ids.length === 0) return { sheet: nextSheet, frozen: [] };
  const before = prevSheet ? computeSheet(prevSheet).rows : {};
  const frozen = [];
  const sheet = {
    ...nextSheet,
    sections: nextSheet.sections.map((sec) => ({
      ...sec,
      rows: (sec.rows || []).map((r) => {
        if (!broken[r.id]) return r;
        frozen.push(r.name);
        const keep = r.id in before ? before[r.id] : round2(r.amount);
        return { ...r, calc: null, amount: keep };
      }),
    })),
  };
  return { sheet, frozen };
}

/**
 * What a row at a given position is allowed to use: everything above it, in
 * order, as [{ ref, sectionId, kind: 'row'|'sec', rowId }].
 *
 * rowId null means a new row, which lands at the end of its section's counted
 * rows (or the end of its under-the-line rows). `below` is the draft value, so
 * flipping the switch in the editor changes what is on offer straight away -
 * a row moved under the line can use its own section's total.
 */
export function refsAbove(sheet, sectionId, rowId, below) {
  const out = [];
  const target = rowId || '__draft__';
  for (const sec of (sheet && sheet.sections) || []) {
    let list = (sec.rows || []).map((r) => (r.id === rowId ? { ...r, below } : r));
    if (sec.id === sectionId && !rowId) list = [...list, { id: '__draft__', below }];
    for (const r of list) {
      if (r.below) continue;
      if (sec.id === sectionId && r.id === target) return out;
      out.push({ ref: rowRef(r.id), sectionId: sec.id, kind: 'row', rowId: r.id });
    }
    out.push({ ref: secRef(sec.id), sectionId: sec.id, kind: 'sec' });
    for (const r of list) {
      if (!r.below) continue;
      if (sec.id === sectionId && r.id === target) return out;
      out.push({ ref: rowRef(r.id), sectionId: sec.id, kind: 'row', rowId: r.id });
    }
  }
  return out;
}

/** The sheet as it would be with this row saved - used for the live preview. */
export function withRow(sheet, sectionId, rowId, draft) {
  const id = rowId || '__draft__';
  return {
    ...sheet,
    sections: sheet.sections.map((sec) => {
      if (sec.id !== sectionId) return sec;
      const rows = sec.rows || [];
      return {
        ...sec,
        rows: rowId
          ? rows.map((r) => (r.id === rowId ? { ...r, ...draft } : r))
          : [...rows, { id, ...draft }],
      };
    }),
  };
}

/** Name of a ref as the person sees it: the row's name, or the section's. */
export function refLabel(sheet, ref) {
  if (!ref) return '';
  const [kind, id] = [ref.slice(0, 3), ref.slice(4)];
  for (const sec of (sheet && sheet.sections) || []) {
    if (kind === 'sec' && sec.id === id) return sec.title || 'Section';
    if (kind === 'row') {
      const r = (sec.rows || []).find((x) => x.id === id);
      if (r) return r.name || 'Untitled';
    }
  }
  return '?';
}

/** The recipe line under a worked-out row, in words. */
export function describeCalc(sheet, calc) {
  if (!calc) return '';
  const L = (r) => refLabel(sheet, r);
  const list = (refs, sep) => {
    const names = refs.map(L);
    if (names.length <= 3) return names.join(sep);
    return names.slice(0, 2).join(sep) + sep + (names.length - 2) + ' more';
  };
  if (calc.type === 'sum') return list(calc.refs || [], ' + ');
  if (calc.type === 'left') {
    const minus = calc.minus || [];
    return L(calc.from) + (minus.length ? ' − ' + list(minus, ' − ') : '');
  }
  if (calc.type === 'share') {
    const part = calc.part === 'pct' ? (Number(calc.pct) || 0) + '%' : SHARE_LABEL[calc.part];
    return part + ' of ' + L(calc.of);
  }
  return '';
}

/** 'A', 'A and B', 'A, B and 2 more' */
export function joinNames(names) {
  if (names.length <= 1) return names[0] || '';
  if (names.length === 2) return names[0] + ' and ' + names[1];
  return names[0] + ', ' + names[1] + ' and ' + (names.length - 2) + ' more';
}

/**
 * Worked-out rows that use any of `refs`, other than rows in `skipRowIds`
 * (the rows being deleted along with them). Used to warn before a delete.
 */
export function dependentsOf(sheet, refs, skipRowIds = []) {
  const out = [];
  for (const sec of (sheet && sheet.sections) || []) {
    for (const r of sec.rows || []) {
      if (!r.calc || skipRowIds.includes(r.id)) continue;
      if (calcRefs(r.calc).some((x) => refs.includes(x))) out.push(r.name || 'Untitled');
    }
  }
  return out;
}

/**
 * Names of worked-out rows that a reorder would break - rows that work today
 * but would end up above something they use. Moves are blocked rather than
 * frozen: a drag should never quietly change what a row means.
 */
export function moveBreaks(sheet, movedSheet) {
  const before = computeSheet(sheet).broken;
  const after = computeSheet(movedSheet).broken;
  const out = [];
  for (const sec of movedSheet.sections || []) {
    for (const r of sec.rows || []) {
      if (after[r.id] && !before[r.id]) out.push(r.name || 'Untitled');
    }
  }
  return out;
}
